import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const splitCommaList = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const joinCommaList = (value) => (Array.isArray(value) ? value.join(', ') : '');

const buildSubmissionAiDraft = (submission) => ({
  ai_category: submission.ai_category || '',
  ai_medium: submission.ai_medium || '',
  ai_style: submission.ai_style || '',
  ai_mood: submission.ai_mood || '',
  ai_subject: submission.ai_subject || '',
  ai_tags: joinCommaList(submission.ai_tags),
  ai_search_keywords: joinCommaList(submission.ai_search_keywords),
  moderation_note: submission.ai_review_note || '',
  publish_corrected_tags: Boolean(submission.ai_review_published),
});

const emptyForm = {
  name: '',
  status: 'upcoming',
  competition_date: '',
  mode: 'online',
  city: '',
  venue: '',
  description: '',
  description_hi: '',
  prize_details: '',
  prize_details_hi: '',
  registration_url: '',
  image_url: '',
  poster_url: '',
  display_order: 0,
  is_active: true,
};

const statusOptions = [
  { value: 'live', label: 'Live competition' },
  { value: 'upcoming', label: 'Upcoming competition' },
  { value: 'past', label: 'Past competition' },
];

const modeOptions = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'hybrid', label: 'Online + Offline' },
];

const foundingPaintersTemplate = {
  name: 'KalaManch Founding Painters Challenge 2026',
  status: 'upcoming',
  competition_date: 'Dates to be announced soon',
  mode: 'online',
  city: 'India',
  venue: 'KalaManch.art',
  registration_url: 'https://www.kalamanch.art',
  image_url: '',
  poster_url: '',
  display_order: 1,
  is_active: true,
  description: `Theme: India Through Your Eyes

Welcome to the first online painting challenge by KalaManch.art, created to discover, showcase, and promote talented painters and visual artists from across India.

Allowed:
- Traditional handmade art
- Watercolor painting
- Acrylic painting
- Oil painting
- Pencil sketching
- Charcoal art
- Ink art
- Handmade canvas artwork
- Mixed media created by hand
- Digital painting created by the artist

Not allowed:
- Fully AI-generated artwork
- AI-generated images submitted as original handmade work
- Copied, traced, stolen, or plagiarized artwork
- Offensive, adult, hateful, illegal, or misleading artwork

Rules:
1. Participants must register on KalaManch.art.
2. Artist profile creation is mandatory.
3. Participants may submit up to 3 artworks.
4. Submitted artworks must be original creations.
5. Digital painting is allowed only when created by the artist.
6. Fully AI-generated artwork is not accepted.
7. Shortlisted participants must provide creation proof before final selection.
8. All submitted artwork remains the property of the respective artist.
9. KalaManch.art will not sell, transfer, claim ownership of, or use submitted artwork for unrelated commercial purposes.
10. KalaManch may display selected entries only for competition-related showcase, result announcement, certificate, social media, website/app feature, or promotion of the artist, with artist credit.
11. Other artists, participants, visitors, and third parties must not copy, trace, reproduce, repost, sell, or misuse any submitted artwork without the artist's permission. Copying another artist's work is a violation of competition rules and may lead to disqualification or account restriction.
12. Jury/KalaManch decisions will be final.

Shortlisted proof may include progress images, 10-30 second creation video, timelapse, brushwork/sketching clip, digital painting screenshots, or screen recording.`,
  description_hi: `थीम: “भारत आपकी नज़रों से”

KalaManch.art द्वारा आयोजित पहला ऑनलाइन पेंटिंग चैलेंज, जिसका उद्देश्य भारत भर के प्रतिभाशाली पेंटर्स और विज़ुअल आर्टिस्ट्स को खोजने, प्रमोट करने और उन्हें मंच देना है।

हर कलाकार भारत को अलग नज़रों से देखता है - रंगों, भावनाओं, संस्कृति, त्योहारों, परंपराओं, गांवों, शहरों, लोगों और कल्पनाओं के माध्यम से।

यह प्रतियोगिता कलाकारों को अपनी कला के माध्यम से भारत को व्यक्त करने का अवसर देती है।

भागीदारी पूरी तरह FREE है।

मान्य कला श्रेणियां:
- वॉटरकलर पेंटिंग
- एक्रिलिक पेंटिंग
- ऑयल पेंटिंग
- पेंसिल स्केचिंग
- चारकोल आर्ट
- इंक आर्ट
- हस्तनिर्मित मिक्स्ड मीडिया
- हैंडमेड कैनवास आर्टवर्क
- कलाकार द्वारा बनाई गई डिजिटल पेंटिंग

मान्य नहीं:
- AI द्वारा पूरी तरह बनाई गई कला
- AI इमेज को हस्तनिर्मित बताना
- कॉपी, ट्रेस, चोरी या प्लेजराइज़्ड आर्टवर्क
- आपत्तिजनक, भ्रामक या गैरकानूनी सामग्री

भागीदारी नियम:
1. प्रतिभागियों को KalaManch.art पर रजिस्टर करना होगा।
2. Artist Profile बनाना जरूरी है।
3. अधिकतम 3 अलग-अलग आर्टवर्क जमा किए जा सकते हैं।
4. भाग लेने के लिए 1 आर्टवर्क पर्याप्त है।
5. जमा की गई कला पूरी तरह मौलिक होनी चाहिए।
6. Fully AI-generated artwork मान्य नहीं है।
7. शॉर्टलिस्टेड कलाकारों से creation proof मांगा जा सकता है।
8. कला का स्वामित्व कलाकार के पास रहेगा।
9. KalaManch केवल प्रतियोगिता, परिणाम, प्रमाणपत्र और कलाकार प्रमोशन हेतु कला दिखा सकता है, कलाकार को क्रेडिट के साथ।
10. बिना अनुमति कला की कॉपी, ट्रेस, रीपोस्ट, बिक्री या दुरुपयोग प्रतिबंधित है।
11. ज्यूरी और KalaManch का निर्णय अंतिम होगा।

सबमिशन गाइडलाइन:
- High-quality artwork image
- Artwork title
- Artwork medium
- Optional artwork story/description

शॉर्टलिस्टेड वेरिफिकेशन:
- Progress images
- 10-30 सेकंड creation video
- Brushwork/sketching clip
- Timelapse video
- Digital painting screenshots
- Screen recording

Nominate 5 Artists Challenge:
भाग लेने के बाद कलाकार 5 अन्य कलाकारों को nominate कर सकते हैं और उन्हें KalaManch.art से जुड़ने के लिए आमंत्रित कर सकते हैं।

KalaManch Android App अब LIVE है:
https://play.google.com/store/apps/details?id=art.kalamanch.app

भागीदारी FREE है। हर टैलेंट को मंच।`,
  prize_details: `Recognition structure:
- Top 25: Shortlisted Artists
- Top 10: Finalists
- Top 3: Grand Winners
- 5-10: Special Mentions

Recognition & Rewards:
- Top three winners will receive Art Kits delivered directly to their provided address
- Featured placement on KalaManch.art
- Digital Winner Certificates
- Social media promotion across KalaManch platforms
- Founding Artist recognition
- Featured opportunities in upcoming KalaManch campaigns
- Priority showcase on KalaManch Android App`,
  prize_details_hi: `प्रतियोगिता संरचना:
- Top 25 Artists: Officially Shortlisted
- Top 10 Artists: Finalists Showcase
- Top 3 Artists: Grand Winners
- Special Jury Mentions भी दिए जा सकते हैं।

विजेताओं को मिल सकता है:
- Art Kits
- Digital Winner Certificates
- KalaManch.art पर Featured Placement
- KalaManch Android App पर Priority Showcase
- Founding Artist Recognition
- Social media promotion
- Future KalaManch campaigns में अवसर

सभी प्रतिभागियों को मिल सकता है:
- Digital Participation Certificate
- Founding Artist Badge
- Community Recognition
- Website और app feature opportunity`,
};

const AdminCompetitions = () => {
  const [competitions, setCompetitions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const [counts, setCounts] = useState({ live: 0, upcoming: 0, past: 0 });
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [aiDraftsBySubmission, setAiDraftsBySubmission] = useState({});
  const [aiDraftLoadingBySubmission, setAiDraftLoadingBySubmission] = useState({});

  const getAiDraft = (submission) => aiDraftsBySubmission[submission.id] || buildSubmissionAiDraft(submission);

  const updateAiDraft = (submissionId, field, value) => {
    setAiDraftsBySubmission((current) => ({
      ...current,
      [submissionId]: {
        ...(current[submissionId] || {}),
        [field]: value,
      },
    }));
  };

  const loadCompetitions = async () => {
    setLoading(true);
    try {
      const [response, submissionResponse] = await Promise.all([
        adminApi.get('/admin/competitions'),
        adminApi.get('/admin/competition-submissions'),
      ]);
      setCompetitions(response.data.competitions || []);
      setCounts(response.data.counts || { live: 0, upcoming: 0, past: 0 });
      setSubmissions(submissionResponse.data.submissions || []);
      setSubmissionCounts(submissionResponse.data.counts || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompetitions();
  }, []);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setError('');
  };

  const useFoundingPaintersTemplate = () => {
    setFormData(foundingPaintersTemplate);
    setEditingId(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitCompetition = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...formData,
        display_order: Number(formData.display_order || 0),
      };
      if (editingId) {
        await adminApi.put(`/admin/competitions/${editingId}`, payload);
      } else {
        await adminApi.post('/admin/competitions', payload);
      }
      resetForm();
      await loadCompetitions();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save competition.');
    } finally {
      setSaving(false);
    }
  };

  const editCompetition = (competition) => {
    setEditingId(competition.id);
    setFormData({
      name: competition.name || '',
      status: competition.status || 'upcoming',
      competition_date: competition.competition_date || '',
      mode: competition.mode || 'online',
      city: competition.city || '',
      venue: competition.venue || '',
      description: competition.description || '',
      description_hi: competition.description_hi || '',
      prize_details: competition.prize_details || '',
      prize_details_hi: competition.prize_details_hi || '',
      registration_url: competition.registration_url || '',
      image_url: competition.image_url || '',
      poster_url: competition.poster_url || '',
      display_order: competition.display_order || 0,
      is_active: Boolean(competition.is_active),
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteCompetition = async (competitionId) => {
    if (!window.confirm('Delete this competition?')) return;
    await adminApi.delete(`/admin/competitions/${competitionId}`);
    await loadCompetitions();
  };

  const toggleCompetitionLive = async (competition) => {
    setError('');
    try {
      await adminApi.patch(`/admin/competitions/${competition.id}/active`, {
        is_active: !competition.is_active,
      });
      await loadCompetitions();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update competition visibility.');
    }
  };

  const updateSubmission = async (submissionId, reviewStatus, isPublic = false) => {
    setSubmissionMessage('');
    await adminApi.put(`/admin/competition-submissions/${submissionId}`, {
      review_status: reviewStatus,
      is_public: isPublic,
      admin_note: '',
    });
    setSubmissionMessage('Submission review updated.');
    await loadCompetitions();
  };

  const autoTagSubmission = async (submission) => {
    setSubmissionMessage('');
    setAiDraftLoadingBySubmission((current) => ({ ...current, [submission.id]: true }));
    try {
      const response = await adminApi.post('/artwork/auto-tag', {
        submission_id: submission.id,
        image_url: submission.artwork_image_url,
        artist_caption: submission.description || '',
        source_collection: 'competition_submissions',
      });
      const aiTags = response.data?.ai_tags || {};
      setAiDraftsBySubmission((current) => ({
        ...current,
        [submission.id]: {
          ...(current[submission.id] || buildSubmissionAiDraft(submission)),
          ai_category: aiTags.category || '',
          ai_medium: aiTags.medium || '',
          ai_style: aiTags.style || '',
          ai_mood: aiTags.mood || '',
          ai_subject: aiTags.subject || '',
          ai_tags: joinCommaList(aiTags.tags),
          ai_search_keywords: joinCommaList(aiTags.search_keywords),
        },
      }));
      setSubmissionMessage(`AI tags generated for "${submission.artwork_title}". Review and publish if needed.`);
      await loadCompetitions();
    } catch (requestError) {
      setSubmissionMessage(requestError.response?.data?.detail || 'Unable to auto-tag this artwork.');
    } finally {
      setAiDraftLoadingBySubmission((current) => ({ ...current, [submission.id]: false }));
    }
  };

  const saveSubmissionAiReview = async (submission, publishCorrectedTags = false) => {
    const draft = getAiDraft(submission);
    setSubmissionMessage('');
    setAiDraftLoadingBySubmission((current) => ({ ...current, [submission.id]: true }));
    try {
      await adminApi.patch(`/admin/competition-submissions/${submission.id}/ai-review`, {
        ai_category: draft.ai_category,
        ai_medium: draft.ai_medium,
        ai_style: draft.ai_style,
        ai_mood: draft.ai_mood,
        ai_subject: draft.ai_subject,
        ai_tags: splitCommaList(draft.ai_tags),
        ai_search_keywords: splitCommaList(draft.ai_search_keywords),
        moderation_note: draft.moderation_note,
        publish_corrected_tags: publishCorrectedTags,
      });
      setAiDraftsBySubmission((current) => ({
        ...current,
        [submission.id]: {
          ...draft,
          publish_corrected_tags: publishCorrectedTags,
        },
      }));
      setSubmissionMessage(
        publishCorrectedTags
          ? `Corrected AI tags published for "${submission.artwork_title}".`
          : `AI review saved for "${submission.artwork_title}".`
      );
      await loadCompetitions();
    } catch (requestError) {
      setSubmissionMessage(requestError.response?.data?.detail || 'Unable to save AI review for this submission.');
    } finally {
      setAiDraftLoadingBySubmission((current) => ({ ...current, [submission.id]: false }));
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await adminApi.post('/uploads/profile-image', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('image_url', response.data.image_url || '');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to upload competition image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const uploadPoster = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await adminApi.post('/uploads/profile-image', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField('poster_url', response.data.image_url || '');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to upload competition poster.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Competitions</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Competition management</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Publish live, upcoming, and past competitions. Public visitors see a clean coming-soon message when a section has no active listings.
            </p>
          </div>
          <button type="button" onClick={resetForm} className="btn-outline">
            Add New Competition
          </button>
          <button type="button" onClick={useFoundingPaintersTemplate} className="btn-primary">
            Use Painters Challenge Template
          </button>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {statusOptions.map((item) => (
            <div key={item.value} className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
              <p className="text-sm font-semibold text-gray-600">{item.label}</p>
              <p className="mt-2 text-3xl font-black text-gray-950">{counts[item.value] || 0}</p>
            </div>
          ))}
        </div>
      </section>

      <form onSubmit={submitCompetition} className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{editingId ? 'Edit competition' : 'Add competition'}</h3>
            <p className="text-gray-600">Name is required. Registration link, image, prize, city, and venue are optional.</p>
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} className="btn-outline">
              Cancel Edit
            </button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <input value={formData.name} onChange={(event) => updateField('name', event.target.value)} placeholder="Competition name" className="rounded-2xl border border-gray-300 px-4 py-3" required />
          <input value={formData.competition_date} onChange={(event) => updateField('competition_date', event.target.value)} placeholder="Date, e.g. 24 June 2026" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <select value={formData.status} onChange={(event) => updateField('status', event.target.value)} className="rounded-2xl border border-gray-300 px-4 py-3">
            {statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select value={formData.mode} onChange={(event) => updateField('mode', event.target.value)} className="rounded-2xl border border-gray-300 px-4 py-3">
            {modeOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <input value={formData.city} onChange={(event) => updateField('city', event.target.value)} placeholder="City / state" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.venue} onChange={(event) => updateField('venue', event.target.value)} placeholder="Venue or platform" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.registration_url} onChange={(event) => updateField('registration_url', event.target.value)} placeholder="Registration URL" className="rounded-2xl border border-gray-300 px-4 py-3 lg:col-span-2" />
          <input type="number" value={formData.display_order} onChange={(event) => updateField('display_order', event.target.value)} placeholder="Display order" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.image_url} onChange={(event) => updateField('image_url', event.target.value)} placeholder="Image URL" className="rounded-2xl border border-gray-300 px-4 py-3 lg:col-span-3" />
          <input value={formData.poster_url} onChange={(event) => updateField('poster_url', event.target.value)} placeholder="Poster URL for competition details page" className="rounded-2xl border border-gray-300 px-4 py-3 lg:col-span-3" />
        </div>

        <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
          <label className="block text-sm font-bold text-gray-900">Upload competition banner/card image from PC or mobile</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={uploadImage}
            className="mt-2 w-full text-sm text-gray-700"
            disabled={uploadingImage}
          />
          <p className="mt-2 text-xs text-gray-500">Use a wide image for banners or a clear square image for cards. Admin uploads skip user malware scan but still validate image type.</p>
          {uploadingImage && <p className="mt-2 text-sm font-semibold text-[#FF6F00]">Uploading image...</p>}
          {formData.image_url && (
            <div className="mt-3 grid gap-3 sm:grid-cols-[160px_1fr] sm:items-center">
              <img src={formData.image_url} alt="Competition preview" className="h-24 w-full rounded-2xl object-cover ring-1 ring-orange-100" />
              <span className="break-all text-xs text-gray-500">{formData.image_url}</span>
            </div>
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
          <label className="block text-sm font-bold text-gray-900">Upload competition poster from PC or mobile</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={uploadPoster}
            className="mt-2 w-full text-sm text-gray-700"
            disabled={uploadingImage}
          />
          <p className="mt-2 text-xs text-gray-500">Use this for vertical promotional posters. It appears inside the competition details popup and can be different from the card/banner image.</p>
          {formData.poster_url && (
            <div className="mt-3 grid gap-3 sm:grid-cols-[120px_1fr] sm:items-center">
              <img src={formData.poster_url} alt="Competition poster preview" className="h-40 w-full rounded-2xl object-cover ring-1 ring-emerald-100" />
              <span className="break-all text-xs text-gray-500">{formData.poster_url}</span>
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <textarea value={formData.description} onChange={(event) => updateField('description', event.target.value)} placeholder="English competition details, eligibility, category, entry notes" className="min-h-[180px] w-full rounded-2xl border border-gray-300 px-4 py-3" />
          <textarea value={formData.description_hi} onChange={(event) => updateField('description_hi', event.target.value)} placeholder="Hindi competition details" className="min-h-[180px] w-full rounded-2xl border border-gray-300 px-4 py-3" />
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <textarea value={formData.prize_details} onChange={(event) => updateField('prize_details', event.target.value)} placeholder="English prize, certificate, showcase, or recognition details" className="min-h-[100px] w-full rounded-2xl border border-gray-300 px-4 py-3" />
          <textarea value={formData.prize_details_hi} onChange={(event) => updateField('prize_details_hi', event.target.value)} placeholder="Hindi prize, certificate, showcase, or recognition details" className="min-h-[100px] w-full rounded-2xl border border-gray-300 px-4 py-3" />
        </div>

        <label className="mt-4 inline-flex items-center gap-3 text-sm font-semibold text-gray-700">
          <input type="checkbox" checked={formData.is_active} onChange={(event) => updateField('is_active', event.target.checked)} />
          Active on public Competition page
        </label>
        <p className="mt-2 text-xs text-gray-500">Untick active to keep a competition saved in admin without showing it on website or app.</p>

        {error && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        <button type="submit" disabled={saving} className="btn-primary mt-5 disabled:cursor-not-allowed disabled:bg-gray-300">
          {saving ? 'Saving...' : editingId ? 'Save Competition Changes' : 'Create Competition'}
        </button>
      </form>

      <section className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#00A78E]">Artwork submissions</p>
            <h3 className="mt-2 text-2xl font-bold text-gray-900">Review competition entries</h3>
            <p className="mt-2 max-w-3xl text-gray-600">
              Artist submissions stay private until admin approves and publishes them. Approved public entries appear on the Competition page and sync to the app.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs font-bold">
            <span className="rounded-2xl bg-orange-50 px-3 py-2 text-[#C45A00]">Pending {submissionCounts.pending_review || 0}</span>
            <span className="rounded-2xl bg-emerald-50 px-3 py-2 text-emerald-700">Approved {submissionCounts.approved || 0}</span>
            <span className="rounded-2xl bg-red-50 px-3 py-2 text-red-700">Rejected {submissionCounts.rejected || 0}</span>
          </div>
        </div>
        {submissionMessage && <p className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">{submissionMessage}</p>}
        {submissions.length === 0 ? (
          <div className="mt-6 rounded-3xl border border-dashed border-emerald-200 p-6 text-center text-gray-500">
            No artwork submissions yet.
          </div>
        ) : (
          <div className="mt-6 grid gap-4">
            {submissions.map((submission) => (
              <article key={submission.id} className="grid gap-5 rounded-3xl border border-gray-100 bg-gray-50 p-4 lg:grid-cols-[140px_1fr_auto]">
                <img src={submission.artwork_image_url} alt={submission.artwork_title} className="h-36 w-full rounded-2xl object-cover ring-1 ring-gray-200" />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-lg font-black text-gray-950">{submission.artwork_title}</h4>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                      submission.review_status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      submission.review_status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-orange-100 text-[#C45A00]'
                    }`}>
                      {submission.review_status || 'pending_review'}
                    </span>
                    {submission.is_public && <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700">Public</span>}
                  </div>
                  <p className="mt-1 text-sm font-semibold text-gray-600">{submission.artist_name} • {submission.medium} • {submission.category || 'No category'}</p>
                  <p className="mt-1 text-xs text-gray-500">{submission.competition_name}</p>
                  {submission.description && <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-700">{submission.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-3 text-sm font-semibold">
                    {submission.process_video_url && <a href={submission.process_video_url} target="_blank" rel="noopener noreferrer" className="text-[#FF6F00]">Process video</a>}
                    {submission.instagram_url && <a href={submission.instagram_url} target="_blank" rel="noopener noreferrer" className="text-[#FF6F00]">Instagram post</a>}
                    {submission.progress_image_urls?.map((url, index) => (
                      <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="text-[#FF6F00]">Progress {index + 1}</a>
                    ))}
                  </div>

                  <div className="mt-4 rounded-3xl border border-indigo-100 bg-white p-4">
                    {(() => {
                      const draft = getAiDraft(submission);
                      const aiBusy = Boolean(aiDraftLoadingBySubmission[submission.id]);
                      return (
                        <>
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-700">Admin Artwork AI Review</p>
                              <p className="mt-1 text-xs text-gray-500">
                                Generate AI tags, edit them, and publish corrected artwork metadata for this competition entry.
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {submission.ai_review_published && (
                                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                                  Published
                                </span>
                              )}
                              {submission.ai_tagged_at && (
                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                                  Tagged {submission.ai_tagged_at.slice(0, 10)}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <input
                              type="text"
                              value={draft.ai_category}
                              onChange={(event) => updateAiDraft(submission.id, 'ai_category', event.target.value)}
                              placeholder="AI category"
                              className="rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                            />
                            <input
                              type="text"
                              value={draft.ai_medium}
                              onChange={(event) => updateAiDraft(submission.id, 'ai_medium', event.target.value)}
                              placeholder="AI medium"
                              className="rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                            />
                            <input
                              type="text"
                              value={draft.ai_style}
                              onChange={(event) => updateAiDraft(submission.id, 'ai_style', event.target.value)}
                              placeholder="AI style"
                              className="rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                            />
                            <input
                              type="text"
                              value={draft.ai_mood}
                              onChange={(event) => updateAiDraft(submission.id, 'ai_mood', event.target.value)}
                              placeholder="AI mood"
                              className="rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                            />
                          </div>

                          <input
                            type="text"
                            value={draft.ai_subject}
                            onChange={(event) => updateAiDraft(submission.id, 'ai_subject', event.target.value)}
                            placeholder="AI subject"
                            className="mt-3 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                          />

                          <textarea
                            value={draft.ai_tags}
                            onChange={(event) => updateAiDraft(submission.id, 'ai_tags', event.target.value)}
                            placeholder="AI tags, comma separated"
                            className="mt-3 min-h-[90px] w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                          />
                          <textarea
                            value={draft.ai_search_keywords}
                            onChange={(event) => updateAiDraft(submission.id, 'ai_search_keywords', event.target.value)}
                            placeholder="AI search keywords, comma separated"
                            className="mt-3 min-h-[90px] w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                          />
                          <textarea
                            value={draft.moderation_note}
                            onChange={(event) => updateAiDraft(submission.id, 'moderation_note', event.target.value)}
                            placeholder="Moderation note for internal artwork review"
                            className="mt-3 min-h-[90px] w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                          />

                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => autoTagSubmission(submission)}
                              disabled={aiBusy}
                              className="rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {aiBusy ? 'Working...' : 'Generate AI tags'}
                            </button>
                            <button
                              type="button"
                              onClick={() => saveSubmissionAiReview(submission, false)}
                              disabled={aiBusy}
                              className="rounded-full border border-orange-200 px-4 py-2 text-sm font-bold text-[#C45A00] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Save AI review
                            </button>
                            <button
                              type="button"
                              onClick={() => saveSubmissionAiReview(submission, true)}
                              disabled={aiBusy}
                              className="rounded-full bg-[#4A148C] px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Publish corrected tags
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 lg:flex-col lg:items-stretch">
                  <button type="button" onClick={() => updateSubmission(submission.id, 'approved', true)} className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white">Approve & Publish</button>
                  <button type="button" onClick={() => updateSubmission(submission.id, 'approved', false)} className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700">Approve Private</button>
                  <button type="button" onClick={() => updateSubmission(submission.id, 'pending_review', false)} className="rounded-full border border-orange-200 px-4 py-2 text-sm font-bold text-[#C45A00]">Keep Pending</button>
                  <button type="button" onClick={() => updateSubmission(submission.id, 'rejected', false)} className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600">Reject</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        {loading ? (
          <div className="p-6 text-gray-500">Loading competitions...</div>
        ) : competitions.length === 0 ? (
          <div className="p-6 text-gray-500">No competitions added yet.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {competitions.map((competition) => (
              <article key={competition.id} className="grid gap-5 p-6 lg:grid-cols-[1fr_auto] lg:items-center">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-900">{competition.name}</h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${competition.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                      {competition.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-[#4A148C]">{competition.status}</span>
                    <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-[#C45A00]">{competition.mode}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-gray-600">
                    {[competition.competition_date, competition.venue, competition.city].filter(Boolean).join(' | ') || 'No date or venue added'}
                  </p>
                  {competition.description && <p className="mt-2 max-w-4xl text-sm text-gray-600">{competition.description}</p>}
                  {competition.registration_url && (
                    <a href={competition.registration_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold text-[#FF6F00]">
                      Open registration link
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => toggleCompetitionLive(competition)}
                    className={`rounded-full px-5 py-2 font-semibold ${
                      competition.is_active
                        ? 'border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                        : 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    {competition.is_active ? 'Make Live Off' : 'Make Live On'}
                  </button>
                  <button type="button" onClick={() => editCompetition(competition)} className="btn-outline">Edit</button>
                  <button type="button" onClick={() => deleteCompetition(competition.id)} className="rounded-full border border-red-200 px-5 py-2 font-semibold text-red-600 hover:bg-red-50">Delete</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminCompetitions;
